// Versioned collector evidence only. Older reports do not imply any evidence state.
const METRICS = ['INP', 'CLS', 'LCP', 'TTFB'];
const STATES = ['unknown', 'unconfirmed', 'measured_attribution'];
const ROLES = ['unknown', 'candidate_cause', 'moved_element', 'affected_container'];
const text = value => typeof value === 'string' ? value : '';

export const evidenceFor = (result, metric) => {
  const evidence = result?.diagnosticEvidence;
  return evidence?.version === 1 && METRICS.includes(metric) && evidence.metric === metric
    && STATES.includes(evidence.state) && Array.isArray(evidence.observations)
    && Array.isArray(evidence.missingLinks) ? evidence : null;
};

export const evidenceEntries = (data) => METRICS.flatMap(metric => {
  const result = data?.[`deep${metric}Results`];
  const evidence = evidenceFor(result, metric);
  return evidence && (data?.unified || String(data?.metric || '').toUpperCase() === metric)
    ? [evidence] : [];
});

const LABELS = {
  cs: {
    title: 'Pozorování měření', scope: 'Rozsah', url: 'URL', device: 'Zařízení',
    unknown: 'Neznámé', unconfirmed: 'Nepotvrzeno', measured_attribution: 'Změřená souvislost',
    candidate_cause: 'Kandidát k prověření', moved_element: 'Posunutý prvek',
    affected_container: 'Dotčený kontejner', gaps: 'Chybějící vazby',
    source: 'Zdroj', details: 'Naměřené údaje', role: 'Role prvku', state: 'Stav',
    subject: 'Subjekt', predicate: 'Vztah', object: 'Objekt', unavailable: 'nedostupné',
    caution: 'Změřená souvislost nedokazuje příčinný mechanismus ani to, že navržená úprava problém vyřeší. Ověřte ji opakovaným měřením a cíleným zásahem.',
    basis: 'Pozorování z počátečního běhu; případný medián více běhů je samostatný souhrn.',
    attempts: 'Pokusy o interakci'
  },
  en: {
    title: 'Measurement observations', scope: 'Scope', url: 'URL', device: 'Device',
    unknown: 'Unknown', unconfirmed: 'Unconfirmed', measured_attribution: 'Measured relationship',
    candidate_cause: 'Candidate to investigate', moved_element: 'Moved element',
    affected_container: 'Affected container', gaps: 'Missing links',
    source: 'Source', details: 'Observed details', role: 'Element role', state: 'State',
    subject: 'Subject', predicate: 'Relationship', object: 'Object', unavailable: 'unavailable',
    caution: 'A measured relationship does not prove a causal mechanism or that a proposed repair will work. Verify with repeated measurement and a controlled intervention.',
    basis: 'Observations from the initial run; any multi-run median is a separate summary.',
    attempts: 'Interaction attempts'
  }
};

const TERMS = {
  'unknown': ['neznámé', 'unknown'],
  'unconfirmed': ['nepotvrzeno', 'unconfirmed'],
  'measured_attribution': ['změřená souvislost', 'measured relationship'],
  'candidate_cause': ['kandidát k prověření', 'candidate to investigate'],
  'moved_element': ['posunutý prvek', 'moved element'],
  'affected_container': ['dotčený kontejner', 'affected container'],
  'redirectMs_unavailable_or_not_applicable': ['přesměrování nedostupné nebo se neuplatní', 'redirects unavailable or not applicable'],
  'dnsMs_unavailable_or_not_applicable': ['DNS nedostupné nebo se neuplatní', 'DNS unavailable or not applicable'],
  'connectMs_unavailable_or_not_applicable': ['spojení nedostupné nebo se neuplatní', 'connection unavailable or not applicable'],
  'tlsMs_unavailable_or_not_applicable': ['TLS nedostupné nebo se neuplatní', 'TLS unavailable or not applicable'],
  'transfer_observed': ['přenos pozorován', 'transfer observed'],
  'transfer_zero_cache_or_timing_restriction': ['nulový přenos: cache nebo omezené časování', 'zero transfer: cache or restricted timing'],
  'cache': ['stav přenosu/cache', 'transfer/cache status'],
  'navigation': ['navigace', 'navigation'],
  'document': ['dokument', 'document'],
  'resource': ['zdroj', 'resource'],
  'lcp-entry': ['záznam LCP', 'LCP entry'],
  'layout_shift': ['posun rozvržení', 'layout shift'],
  'first-byte-after-fetch': ['první bajt po začátku načítání', 'first byte after fetch'],
  'response-interval-for': ['interval odpovědi pro', 'response interval for'],
  'rendered-after-navigation-response': ['vykresleno po odpovědi navigace', 'rendered after navigation response'],
  'reports-duration-for': ['hlásí délku pro', 'reports duration for'],
  'has_event_timing': ['má navázaný Event Timing', 'has linked Event Timing'],
  'received_trusted_target_input': ['přijala důvěryhodný vstup', 'received trusted target input'],
  'layout_shift_observed_without_source_geometry': ['posun bez geometrie zdroje', 'shift observed without source geometry'],
  'mutation_insert_observed_near_layout_shift': ['vložení prvku poblíž posunu v čase', 'insertion observed near layout shift'],
  'resize_observed_near_layout_shift': ['změna velikosti poblíž posunu v čase', 'resize observed near layout shift'],
  'image_load_observed_near_layout_shift': ['načtení obrázku poblíž posunu v čase', 'image load observed near layout shift'],
  'request_wait_boundary_unavailable': ['hranice čekání na odpověď není dostupná', 'request wait boundary unavailable'],
  'navigation_response_timing_unavailable': ['časování navigace není dostupné', 'navigation response timing unavailable'],
  'server_timing_unavailable': ['Server-Timing není dostupné', 'Server-Timing unavailable'],
  'cache_status_unverified': ['stav cache není ověřen', 'cache status unverified'],
  'protocol_unavailable': ['protokol není dostupný', 'protocol unavailable'],
  'trusted_target_input': ['důvěryhodný vstup do cíle', 'trusted target input'],
  'event_to_shift_mechanism_unverified': ['mechanismus mezi událostí a posunem není ověřen', 'event-to-shift mechanism unverified'],
  'source_movement_geometry_missing': ['chybí geometrie pohybu prvku', 'element movement geometry missing'],
  'lcp_entry_not_observed': ['nebyl pozorován záznam LCP', 'LCP entry not observed'],
  'lcp_resource_timing_match_unavailable': ['časování zdroje LCP nelze přiřadit', 'LCP resource timing match unavailable'],
  'lcp_resource_timing_match_incompatible': ['časování zdroje LCP se neshoduje', 'LCP resource timing incompatible'],
  'render_work_mechanism_unverified': ['mechanismus vykreslování není ověřen', 'rendering mechanism unverified'],
  'mechanism_of_resource_delay_unverified': ['mechanismus prodlevy zdroje není ověřen', 'resource delay mechanism unverified'],
  'time': ['čas', 'time'],
  'value': ['hodnota posunu', 'shift value'],
  'requestWaitMs': ['čekání od požadavku do prvního bajtu', 'request-to-first-byte wait'],
  'ttfbMs': ['TTFB navigace', 'navigation TTFB'],
  'redirectMs': ['přesměrování', 'redirects'],
  'dnsMs': ['DNS', 'DNS'],
  'connectMs': ['spojení', 'connection'],
  'tlsMs': ['TLS', 'TLS'],
  'responseMs': ['stažení odpovědi', 'response download'],
  'durationMs': ['délka', 'duration'],
  'transferSizeBytes': ['přenesená data', 'transferred bytes'],
  'postLoadToLcpMs': ['od načtení zdroje do LCP', 'resource load to LCP'],
  'discoveryDelayMs': ['prodleva nalezení zdroje', 'resource discovery delay'],
  'resourceDurationMs': ['délka načítání zdroje', 'resource load duration'],
  'first-byte-after-fetch': ['první bajt po začátku načítání', 'first byte after fetch'],
  'rendered-at': ['vykresleno v čase', 'rendered at'],
  'references-timed-resource': ['odkazuje na časovaný zdroj', 'references timed resource'],
  'has-observed-resource-interval': ['má pozorovaný interval zdroje', 'has observed resource interval'],
  'source_geometry_moved_in_layout_shift': ['geometrie prvku se změnila při posunu', 'element geometry moved during layout shift'],
  'source_listed_in_layout_shift': ['uveden ve zdrojích posunu', 'listed among shift sources'],
  'request_wait_cannot_isolate_backend_work': ['čekání na odpověď nerozlišuje práci backendu', 'request wait cannot isolate backend work'],
  'controlled_intervention_and_repeat_comparison': ['chybí kontrolovaný zásah a opakované porovnání', 'controlled intervention and repeated comparison missing'],
  'causal_mechanism_unverified': ['příčinný mechanismus není ověřen', 'causal mechanism unverified'],
  'causal_mechanism_not_measured': ['příčinný mechanismus nebyl změřen', 'causal mechanism not measured'],
  'backend_trace_not_collected': ['nebyla získána stopa backendu', 'backend trace not collected'],
  'event_timing_link': ['chybí vazba na Event Timing', 'Event Timing link missing'],
  'interaction_linked_work_contribution': ['chybí vazba práce na interakci', 'interaction-linked work contribution missing'],
  'lcp_resource_timing_match_ambiguous': ['přiřazení LCP zdroje je nejednoznačné', 'LCP resource match ambiguous'],
  'source_geometry_missing': ['chybí geometrie zdroje', 'source geometry missing']
};

export const evidenceLabels = language => LABELS[language === 'en' ? 'en' : 'cs'];
export const evidenceTerm = (term, language) => TERMS[term]?.[language === 'en' ? 1 : 0] || text(term).replace(/_/g, ' ');
export const evidenceMissingTerm = (term, language) => term === 'trusted_target_input'
  ? (language === 'en' ? 'trusted target input missing' : 'chybí důvěryhodný vstup do cíle')
  : evidenceTerm(term, language);
export const evidenceDetails = (details, language) => Object.entries(details && typeof details === 'object' && !Array.isArray(details) ? details : {})
  .filter(([, value]) => value !== undefined)
  .map(([key, value]) => {
    const label = evidenceTerm(key, language);
    if (value == null) return `${label}: ${evidenceLabels(language).unavailable}`;
    if (typeof value === 'number') return `${label}: ${value}${key.endsWith('Ms') ? ' ms' : key.endsWith('Bytes') ? ' B' : ''}`;
    if (typeof value === 'string') return `${label}: ${TERMS[value]?.[language === 'en' ? 1 : 0] || value}`;
    if (typeof value === 'boolean') return `${label}: ${value}`;
    return `${label}: ${JSON.stringify(value)}`;
  });

export const evidenceAttempts = (evidence, language) => evidence.metric === 'INP' && Array.isArray(evidence.attempts)
  ? evidence.attempts.map(attempt => evidenceDetails(attempt, language)) : [];

export const escapeEvidenceHtml = value => String(value ?? '').replace(/[&<>"']/g, char => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
}[char]));

export const renderEvidenceHTML = (data, language = 'cs', theme = 'dark') => {
  const entries = evidenceEntries(data);
  if (!entries.length) return '';
  const labels = evidenceLabels(language);
  const esc = escapeEvidenceHtml;
  const dark = theme === 'dark';
  const ink = dark ? '#f1f5f9' : '#172033';
  const muted = dark ? '#cbd5e1' : '#475569';
  const bg = dark ? '#17233a' : '#f1f5f9';
  return entries.map(evidence => `<section aria-label="${esc(labels.title)} ${esc(evidence.metric)}" style="margin:20px 0;padding:18px;border:1px solid ${dark ? '#64748b' : '#94a3b8'};background:${bg};color:${ink};border-radius:8px;overflow-wrap:anywhere">
    <h2 style="color:${ink};margin:0 0 10px">${esc(labels.title)} · ${esc(evidence.metric)}</h2>
    <p style="color:${muted}">${esc(labels.scope)}: ${esc(labels.url)} ${esc(evidence.scope?.url || labels.unavailable)} · ${esc(labels.device)} ${esc(evidence.scope?.device || labels.unavailable)} · ${esc(labels.state)}: ${esc(evidenceTerm(evidence.state, language))}</p>
    <p style="color:${muted}">${esc(labels.basis)}</p>
    ${(evidence.observations || []).map(o => `<div style="padding:10px;margin:8px 0;border:1px solid ${dark ? '#475569' : '#cbd5e1'};color:${ink}">
      <p>${esc(labels.subject)}: ${esc(o.subject)} · ${esc(labels.predicate)}: ${esc(evidenceTerm(o.predicate, language))} (${esc(o.predicate)}) · ${esc(labels.object)}: ${esc(o.object)}</p>
      <p>${esc(labels.state)}: ${esc(evidenceTerm(o.level, language))} · ${esc(labels.role)}: ${esc(evidenceTerm(o.elementRole || 'unknown', language))} · ${esc(labels.source)}: ${esc(o.sourceRef || labels.unavailable)}</p>
      ${evidenceDetails(o.details, language).map(line => `<div>${esc(line)}</div>`).join('')}
      ${o.missingLinks?.length ? `<p>${esc(labels.gaps)}: ${o.missingLinks.map(link => esc(evidenceMissingTerm(link, language))).join('; ')}</p>` : ''}
    </div>`).join('')}
    ${evidenceAttempts(evidence, language).length ? `<h3 style="color:${ink}">${esc(labels.attempts)}</h3>${evidenceAttempts(evidence, language).map(lines => `<div style="border:1px solid ${dark ? '#475569' : '#cbd5e1'};padding:8px;margin:6px 0">${lines.map(line => `<div>${esc(line)}</div>`).join('')}</div>`).join('')}` : ''}
    <p>${esc(labels.gaps)}: ${evidence.missingLinks.length ? evidence.missingLinks.map(link => esc(evidenceMissingTerm(link, language))).join('; ') : esc(labels.unavailable)}</p>
    <p style="color:${muted}">${esc(labels.caution)}</p>
  </section>`).join('');
};